// HTTP service
// wraps api calls with common options (headers, json parsing, etc.)

// global event bus
import Vue from 'vue'
const bus = new Vue()

const API_URL = process.env.API_URL

/* global AbortController */
// abort API calls that hang past this many ms (unstable / unresponsive backend)
const REQUEST_TIMEOUT = 30000

export default {
  baseUrl: API_URL,
  bus: bus,
  get (url, headers, payload, silent) {
    return this.fetchWrapper('GET', url, headers, payload, silent)
  },
  post (url, headers, payload, silent) {
    return this.fetchWrapper('POST', url, headers, payload, silent)
  },
  patch (url, headers, payload, silent) {
    return this.fetchWrapper('PATCH', url, headers, payload, silent)
  },
  delete (url, headers, silent) {
    return this.fetchWrapper('DELETE', url, headers, undefined, silent)
  },
  fetchWrapper (method, url, headers, payload, silent) {
    const opts = {
      method,
      url: buildUrl(url),
      headers: buildHeaders(headers),
      payload,
      silent
    }
    bus.$emit('outbound-request', opts)
    return makeRequestPromise.bind(this)(opts)
  }
}

function buildUrl (url) {
  return API_URL + url
}

function buildHeaders (extraHeaders) {
  const baseHeaders = {
    Accept: 'application/json'
  }
  const result = {}
  Object.assign(result, baseHeaders, extraHeaders)
  return result
}

function checkStatus (response) {
  if (response.status >= 200 && response.status < 300) {
    return response
  } else {
    const error = new Error(response.statusText)
    error.response = response
    throw error
  }
}

function queryParams (params) {
  return Object.keys(params)
    .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
    .join('&')
}

// Wrap fetch with a timeout so an unresponsive backend fails fast with a
// clear error instead of leaving the request pending for minutes.
function fetchWithTimeout (url, options) {
  if (typeof AbortController !== 'undefined') {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)
    return fetch(url, Object.assign({}, options, { signal: controller.signal }))
      .then((response) => {
        clearTimeout(timer)
        return response
      }, (error) => {
        clearTimeout(timer)
        if (error && error.name === 'AbortError') {
          const timeoutError = new Error('Request timed out')
          timeoutError.timeout = true
          throw timeoutError
        }
        throw error
      })
  }
  // fallback when AbortController is unavailable: reject on timeout (the
  // underlying request can't be cancelled, but the caller is unblocked)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const timeoutError = new Error('Request timed out')
      timeoutError.timeout = true
      reject(timeoutError)
    }, REQUEST_TIMEOUT)
    fetch(url, options).then((response) => {
      clearTimeout(timer)
      resolve(response)
    }, (error) => {
      clearTimeout(timer)
      reject(error)
    })
  })
}

function makeRequestPromise (opts) {
  const options = {
    method: opts.method,
    headers: opts.headers
  }
  if (options.method === 'POST' || options.method === 'PATCH') {
    options.body = JSON.stringify(opts.payload)
    options.headers['Content-Type'] = 'application/json'
  }
  let url
  if ((options.method === 'GET' || options.method === 'DELETE') &&
      typeof opts.payload !== 'undefined') {
    url = `${opts.url}?${queryParams(opts.payload)}`
  } else {
    url = opts.url
  }
  return fetchWithTimeout(url, options)
    .then(checkStatus)
    .then((response) => {
      this.bus.$emit('request-success', response, opts)
      return response.json().catch(() => {
        return {}
      })
    }).catch((error) => {
      this.bus.$emit('request-error', error, opts)
      throw error
    })
}

export {
  buildUrl
}
